import { z } from 'zod';

/**
 * The NOYAU of a person, in the sense of the PMS method (noyau.md §2), and the
 * shapes every lot of issue #145 reads - the contract of issue #147.
 *
 * ## Why this file imports nothing but Zod
 *
 * It is the base the others stand on: the experience's content cites it (the
 * Noyau block and its modes, a condition on `@noyau`), the session's view and
 * its SSE frame cite it, the stored determination cites it. Every one of those
 * files is imported by some other, and a module of Zod constants caught in a
 * cycle throws at import time - so the dependency runs one way, from them to
 * here, and this file cites nothing of theirs.
 *
 * ## Identifiers are English, names are the deposited ones
 *
 * `first-choice`, `no-extracts`, `principal`: a stored enum is code, and the
 * French sentence beside each of them lives on the screens. The one French thing
 * here is the NAME of each noyau (« Souverain Magicien »), and it is not a label
 * this repository chose: the names are deposited and do not change (§2.1), so
 * they are data, like the colours beside them.
 */

/* ---------------------------------------------------------------- the grid */

/**
 * The six noyaux of §2.1, IN THE ORDER OF THE TABLE: every reader lists them in
 * this order - the ranking's tie is broken by a draw and never by it (§4.2), but
 * Réglages > Noyaux, the filter of Sessions and the preview's default pair all
 * read it.
 *
 * A code is the TRIPLET of energies in the order Pourquoi, Comment, Quoi: each
 * level is carried by one energy, one of each (S Souverain, G Guerrier,
 * P Producteur), hence six permutations and no more.
 */
export const NOYAU_CODES = ['SGP', 'SPG', 'GSP', 'GPS', 'PGS', 'PSG'] as const;

export const noyauCodeSchema = z.enum(NOYAU_CODES);
export type NoyauCode = z.infer<typeof noyauCodeSchema>;

/** One noyau of the table: its triplet, its deposited name, its official colour. */
export type NoyauDefinition = {
  readonly code: NoyauCode;
  /** Deposited - « Souverain Magicien » - and what `@noyau` renders (N11). */
  readonly name: string;
  /** The official colour, `#rrggbb`. The operator sees it, the person never does (N19). */
  readonly color: string;
};

/**
 * The table of §2.1, the one place it is written.
 *
 * It lives in CODE and not in the base: the names are deposited, the colours are
 * official, and a table an operator could edit would be a method an operator
 * could rewrite. What an operator DOES write - the texts a specialist provides -
 * is the versioned object of `noyau-texts.ts`, and it carries neither name nor
 * triplet (§6.2).
 */
export const NOYAUX: readonly NoyauDefinition[] = [
  { code: 'SGP', name: 'Souverain Magicien', color: '#312783' },
  { code: 'SPG', name: 'Souverain Juriste', color: '#009FE3' },
  { code: 'GSP', name: 'Guerrier Cosmique', color: '#E6007E' },
  { code: 'GPS', name: 'Guerrier Humain', color: '#E30613' },
  { code: 'PGS', name: 'Producteur Séducteur', color: '#EFD500' },
  { code: 'PSG', name: 'Producteur Réinventeur', color: '#009640' },
];

const NOYAU_BY_CODE: ReadonlyMap<NoyauCode, NoyauDefinition> = new Map(
  NOYAUX.map((noyau) => [noyau.code, noyau]),
);

/** Whether a string is one of the six triplets - the narrowing every free text needs. */
export function isNoyauCode(value: string): value is NoyauCode {
  return (NOYAU_CODES as readonly string[]).includes(value);
}

/**
 * The row of the table for one code.
 *
 * It throws on a code the table does not hold, which the type makes unreachable:
 * `NoyauCode` IS the list of the table's codes, and a throw is the honest answer
 * to a value that got past the compiler through a cast.
 */
export function noyauOf(code: NoyauCode): NoyauDefinition {
  const noyau = NOYAU_BY_CODE.get(code);
  if (noyau === undefined) throw new Error(`Not a noyau of the table: ${code}`);
  return noyau;
}

/**
 * The OPPOSITE of a noyau: the MIRROR of Patrick Mathieu (§2.2), which swaps the
 * Pourquoi and the Quoi and keeps the Comment. SGP and PGS, SPG and GPS, GSP and
 * PSG: one opposite per noyau, and the relation is symmetric.
 *
 * The ONE place the rule is said. The rule of the original request - « aucune
 * lettre à la même place » - gives two opposites per noyau and is set aside
 * (§2.2); so are the pairs of the doshas.
 *
 * Swapping the first and the last letter of a triplet is reading it backwards,
 * and the reversal of a permutation of S, G, P is a permutation of S, G, P - so
 * the guard below cannot fire, and it is there for the compiler, which does not
 * know that.
 */
export function oppositeNoyau(code: NoyauCode): NoyauCode {
  const mirror = [...code].reverse().join('');
  if (!isNoyauCode(mirror)) throw new Error(`The mirror of ${code} is not a noyau: ${mirror}`);
  return mirror;
}

/* --------------------------------------------------------------- the block */

/**
 * The three MODES of the Noyau block (§3.2), the ninth type of block of §9.2.
 *
 * - `first-choice` - the first text in two versions, the principal and the
 *   second of the determination; the card taken gives the RETAINED noyau when
 *   their notes are within the block's gap threshold, and the principal is
 *   retained beyond it (§3.8, issue #169).
 * - `second-choice` - the second text in two versions, the retained noyau and
 *   its opposite; the card taken is recorded and changes nothing.
 * - `portrait` - the portrait of the retained noyau, in rich text.
 */
export const noyauBlockModeSchema = z.enum(['first-choice', 'second-choice', 'portrait']);
export type NoyauBlockMode = z.infer<typeof noyauBlockModeSchema>;

/** The two modes whose cards are the page's only way out (§3.4). */
export const noyauChoiceModeSchema = noyauBlockModeSchema.exclude(['portrait']);
export type NoyauChoiceMode = z.infer<typeof noyauChoiceModeSchema>;

/**
 * The GAP THRESHOLD of the first choice (noyau.md §3.8, E1 to E4, N68 to N70;
 * issue #169), in POINTS: the card taken gives the retained noyau only when
 * the principal and the second are at most this many points apart - two
 * integers of 0 to 100, so « 5 % » reads 5 points (E1). Beyond it the principal
 * is retained, and the card is recorded without changing anything (E2).
 *
 * A field of EVERY Noyau block (E3, N68), read by the first choice alone: a
 * block moved to another mode keeps its threshold, without effect. At 0, only
 * a tie lets the person decide; at 100, they always do. 5 by default, which is
 * also what a version stored before #169 reads (E4): the rule applies from the
 * deployment, with no new version to save.
 *
 * The bounds are the SCHEMA's, so a save and the MCP refuse the same value -
 * out of bounds or not an integer - and the inspector clamps to the same two
 * numbers while one types.
 */
export const NOYAU_MAX_GAP_MIN = 0;
export const NOYAU_MAX_GAP_MAX = 100;
export const NOYAU_MAX_GAP_DEFAULT = 5;

export const noyauMaxGapSchema = z.number().int().min(NOYAU_MAX_GAP_MIN).max(NOYAU_MAX_GAP_MAX);

/**
 * The label of the button that CONFIRMS a choice (§5.1, N29): fixed, the words
 * of the review of 2026-09-25, and not a setting of the inspector.
 *
 * Here and not in the reader alone, because two ends spell it: the reader draws
 * it under the two cards, and the server freezes it beside the traversal of the
 * page it leaves (`pressedLabelOf`), so the parcours of Sessions reads « quittée
 * par : Choisir ce texte » in the words the person pressed.
 */
export const NOYAU_CHOICE_BUTTON_LABEL = 'Choisir ce texte';

/**
 * The token of `@noyau` (§3.3, N12): no identifier, since an experience has ONE
 * retained noyau. The other four tokens of §8.6 carry an address; this one is
 * the whole of its own address.
 *
 * Here and not in `experience/variables.ts`, which reads it too: the modules that
 * scan a page for it (`experience/noyau.ts`, and through it `play.ts`) sit UNDER
 * `variables.ts`, and a constant they share has to live below all three.
 */
export const NOYAU_TOKEN = '{{noyau}}';

/**
 * The `id` of `@noyau` in the `{ kind, id }` every reader of a variable takes
 * (`ExperienceVariable`).
 *
 * A CONSTANT, since the token carries no address: « the retained noyau » is one
 * thing per experience. Spelled once so the parse, the reach rule and a
 * condition on `@noyau` all hand the same pair around, and so a problem's
 * `variableId` - a non-empty string - has something to say.
 */
export const NOYAU_VARIABLE_ID = 'noyau';

/* ------------------------------------------------------- the determination */

/**
 * The four STATES of a determination (§4.5).
 *
 * - `pending` - the analyst is being called, in the background.
 * - `rendered` - six notes came back and were ranked: a principal and a second.
 * - `no-extracts` - the protocol closed with no live extract; no call was made.
 * - `failed` - two calls failed, whatever the failure (N4).
 *
 * In the last two the sequence is SKIPPED: the pages that depend on the noyau
 * stop being playable (`isPlayablePage`).
 */
export const noyauDeterminationStateSchema = z.enum([
  'pending',
  'rendered',
  'no-extracts',
  'failed',
]);
export type NoyauDeterminationState = z.infer<typeof noyauDeterminationStateSchema>;

/** The three states a determination SETTLES in - what the `noyau` frame announces. */
export const noyauSettledStateSchema = noyauDeterminationStateSchema.exclude(['pending']);
export type NoyauSettledState = z.infer<typeof noyauSettledStateSchema>;

/** Whether the sequence is skipped in this state (§4.5): nothing to show, nothing invented. */
export function isNoyauSkipped(state: NoyauDeterminationState | null): boolean {
  return state === 'no-extracts' || state === 'failed';
}

/** One sentence of justification, bounded like the scribe's (a defect meets it, never a model on purpose). */
export const NOYAU_JUSTIFICATION_MAX = 600;

/**
 * One note of the analyst (§4.2, N1): an integer from 0 to 100, and ONE sentence
 * of justification - what will be re-read to understand a ranking.
 *
 * The justification is REQUIRED, where the scribe's defaults to the empty
 * string: the contract of §4.2 asks for it, and an answer without it is an
 * answer of the wrong shape, which the analyst's retry exists for (N4).
 *
 * Not `.strict()`, like every shape a model produces (`turn.ts`): a model that
 * adds a key must not cost a determination, a model that drops a note must.
 */
export const noyauScoreSchema = z.object({
  noyau: noyauCodeSchema,
  score: z.number().int().min(0).max(100),
  justification: z.string().trim().min(1).max(NOYAU_JUSTIFICATION_MAX),
});
export type NoyauScore = z.infer<typeof noyauScoreSchema>;

/**
 * The SIX notes of a determination, one per triplet of the table, and nothing
 * else - the stored half of the analyst's answer.
 *
 * A seventh note, a missing noyau or a noyau noted twice are refused here, so a
 * ranking never has to wonder which of two notes of one noyau to believe.
 */
export const noyauScoresSchema = z
  .array(noyauScoreSchema)
  .length(NOYAU_CODES.length)
  .superRefine((scores, ctx) => {
    const seen = new Set<NoyauCode>();
    scores.forEach((entry, index) => {
      if (seen.has(entry.noyau)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [index, 'noyau'],
          message: `The noyau ${entry.noyau} is noted twice: one note per triplet.`,
        });
      }
      seen.add(entry.noyau);
    });
  });

/**
 * What the ANALYST answers (§4.2) - a model's output, and therefore an external
 * input: parsed before anything believes it (the repository's boundary rule).
 *
 * « Exactement six entrées, une par triplet de la table de §2.1, rien d'autre. »
 * An answer that fails this parse is an answer of the wrong shape, retried once
 * (N4) and then `failed`. The analyst does NOT rank: the code does
 * (`rankNoyaux`), with a draw for a tie.
 */
export const noyauAnalystOutputSchema = z.object({
  scores: noyauScoresSchema,
});
export type NoyauAnalystOutput = z.infer<typeof noyauAnalystOutputSchema>;

/**
 * The two places a determination decides (§4.2): the PRINCIPAL, whose first text
 * faces the SECOND's at the first choice.
 */
export const noyauRankPlaceSchema = z.enum(['principal', 'second']);
export type NoyauRankPlace = z.infer<typeof noyauRankPlaceSchema>;

/**
 * A TIE that decided a place, and how the draw settled it (§4.2, N2) - recorded,
 * because an order fixed in advance would always favour the same noyau and a
 * measure that cannot see the draw cannot correct for it.
 *
 * `place` is the highest place the tie decided: `principal` when two or more
 * noyaux share the best note (the draw then gives both places), `second` when
 * the best is alone and two or more share the next. `tied` is the tied group in
 * the order of the table, `order` the same group in the order the draw put it -
 * its first member took `place`.
 */
export const noyauDrawSchema = z.object({
  place: noyauRankPlaceSchema,
  tied: z.array(noyauCodeSchema).min(2).max(NOYAU_CODES.length),
  order: z.array(noyauCodeSchema).min(2).max(NOYAU_CODES.length),
});
export type NoyauDraw = z.infer<typeof noyauDrawSchema>;

/* --------------------------------------------------------------- the cards */

/**
 * The OPAQUE identifier of a card of a choice (§5.1, N28): a UUID, DRAWN by
 * `randomUUID()` when the pair is first shown - never derived from the noyau,
 * the session, the place of the card or anything a reader could compute back.
 *
 * The browser receives the two texts and two of these, and nothing else: the
 * code of the noyau behind a card, the principal and the second never leave the
 * server, so « à l'aveugle » holds for whoever opens the browser's tools. The
 * advance carries the identifier of the card taken, and the server knows which
 * noyau it designates.
 *
 * Closed HERE, before any card is written: a `cardId: 'SGP'` would hand the
 * choice to the browser, and tightening the shape once rows exist in
 * `session_noyau_choices.cards` would make those rows stop parsing.
 */
export const noyauCardIdSchema = z.string().uuid();

/**
 * One card of a choice as the SERVER keeps it: its opaque identifier and the
 * noyau it stands for. Never served to the browser (N28).
 */
export const noyauChoiceCardSchema = z.object({
  cardId: noyauCardIdSchema,
  noyau: noyauCodeSchema,
});
export type NoyauChoiceCard = z.infer<typeof noyauChoiceCardSchema>;

/**
 * The two cards of a choice IN THE ORDER SHOWN (§5.1) - left on a screen, on top
 * on a telephone. Drawn at the first arrival on the page, frozen for a reprise
 * and recorded, so the measure can check that no place is favoured.
 *
 * A pair of the SAME noyau is refused: the first choice faces two distinct
 * places of a ranking, the second a noyau and its mirror, and a triplet is never
 * its own mirror.
 */
export const noyauChoiceCardsSchema = z
  .tuple([noyauChoiceCardSchema, noyauChoiceCardSchema])
  .refine(([first, second]) => first.noyau !== second.noyau, {
    message: 'The two cards of a choice stand for two different noyaux.',
  })
  .refine(([first, second]) => first.cardId !== second.cardId, {
    message: 'The two cards of a choice carry two different identifiers.',
  });
export type NoyauChoiceCards = z.infer<typeof noyauChoiceCardsSchema>;

/* ------------------------------------------------------------- the frame */

/**
 * The `noyau` frame of the session's SSE channel (§4.6, N5): the determination
 * is SETTLED, in one of its three final states.
 *
 * The wait is at the button and holds no request open: an advance whose
 * destination depends on the noyau answers `noyau-pending` while the analyst
 * runs, and the client REPLAYS it on this frame. The state rides along so an
 * open tab knows, without a round trip, whether the sequence will play or be
 * skipped.
 *
 * Not `.strict()`, like every frame of the channel (`session.ts`): an older tab
 * strips a field it does not know and drops a `type` it does not know.
 */
export const sessionNoyauEventSchema = z.object({
  type: z.literal('noyau'),
  state: noyauSettledStateSchema,
});
export type SessionNoyauEvent = z.infer<typeof sessionNoyauEventSchema>;
